import React, { useState } from "react";

// Textbook Book Review pattern: a bounded, read-only index over saved Canon,
// with visible structural readiness and direct paths back to writing.
// A Review indicator is not literary approval or a publication lock.
const REVIEW_INDEX_PAGE_SIZE = 12;

function clean(value) {
  return String(value ?? "").trim();
}
function safeArray(value) {
  return Array.isArray(value) ? value : [];
}
function readableCanon(scene) {
  if (!scene) return false;
  if (clean(scene.text || scene.body || scene.content || scene.sceneText)) return true;
  return safeArray(scene.manuscriptContent?.blocks).some((block) =>
    safeArray(block?.runs).some((run) => clean(run?.text))
  );
}

export default function NovelStoryBookReview({
  title = "",
  chapters = [],
  frontMatter = [],
  backMatter = [],
  unassignedSceneIds = [],
  sceneById,
  isFR = false,
  onOpenSection,
  onOpenChapter,
  onBackToBookStructure,
}) {
  const [indexPage, setIndexPage] = useState(0);
  const [jumpChapterId, setJumpChapterId] = useState("");

  const manuscriptChapters = safeArray(chapters);
  const frontItems = safeArray(frontMatter);
  const backItems = safeArray(backMatter);
  const unassigned = safeArray(unassignedSceneIds);
  const chapterChecks = manuscriptChapters.map((chapter, index) => {
        const ids = safeArray(chapter.sceneIds);
        const savedScenes = ids.map((id) => sceneById?.get(id) || null);
        const missingCount = savedScenes.filter((scene) => !scene).length;
        const noTextCount = savedScenes.filter((scene) => scene && !readableCanon(scene)).length;
        const named = Boolean(clean(chapter.title));
        return {
          id: chapter.id,
          label: clean(chapter.title) || (isFR ? `Chapitre ${index + 1}` : `Chapter ${index + 1}`),
          sceneCount: ids.length,
          missingCount,
          noTextCount,
          ready: named && ids.length > 0 && missingCount === 0 && noTextCount === 0,
        };
      });
  const readyCount = chapterChecks.filter((chapter) => chapter.ready).length;
  const needsAttentionCount = chapterChecks.length - readyCount;
  const pageCount = Math.max(1, Math.ceil(chapterChecks.length / REVIEW_INDEX_PAGE_SIZE));
  const safeIndexPage = Math.min(indexPage, pageCount - 1);
  const visibleChapters = chapterChecks.slice(
    safeIndexPage * REVIEW_INDEX_PAGE_SIZE,
    (safeIndexPage + 1) * REVIEW_INDEX_PAGE_SIZE
  );

  function openSection(sectionId) {
    if (typeof onOpenSection === "function") onOpenSection(sectionId);
  }

  return (
    <section
      data-novel-story-review-workspace="true"
      style={{ display: "grid", gap: 16 }}
    >
      <header
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 18,
          border: "1.5px solid rgba(5,150,105,0.28)",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.1em", color: "#047857" }}>
          {isFR ? "NEXTSCENES · RÉVISER VOTRE LIVRE" : "NEXTSCENES · REVIEW YOUR BOOK"}
        </div>
        <h2 style={{ margin: "8px 0" }}>{clean(title) || (isFR ? "Livre sans titre" : "Untitled Book")}</h2>
        <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
          {isFR
            ? "Révision privée, en lecture seule, du manuscrit Canon enregistré. Cette étape ne valide, ne publie et ne verrouille rien."
            : "Private, read-only review of the saved Canon manuscript. This stage does not approve, publish, or lock the book."}
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="ns-button ns-button-primary" type="button"
            data-novel-story-review-open-book="true"
            onClick={() => openSection("front")}>
            {isFR ? "Lire le livre" : "Read Book"}
          </button>
          <button className="ns-button ns-button-outline" type="button"
            onClick={onBackToBookStructure}>
            {isFR ? "← Structure du livre" : "← Back to Book Structure"}
          </button>
        </div>
      </header>

      <div
        data-novel-story-review-readiness="true"
        className="ns-card"
        style={{ padding: 20, borderRadius: 18 }}
      >
        <h3 style={{ margin: "0 0 7px" }}>
          {isFR ? "État structurel du manuscrit" : "Manuscript Structure Check"}
        </h3>
        <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
          {isFR
            ? "Ces contrôles détectent seulement du contenu manquant; ils ne jugent ni le style, ni la continuité narrative, ni la qualité littéraire."
            : "These checks identify missing saved content only. They do not assess prose, story continuity, or literary quality."}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <span className="ns-badge">{chapterChecks.length} {isFR ? "chapitres" : "Chapters"}</span>
          <span className="ns-badge">{readyCount} {isFR ? "prêts à lire" : "Ready to review"}</span>
          <span className="ns-badge">{needsAttentionCount} {isFR ? "à vérifier" : "Need attention"}</span>
          <span className="ns-badge">{unassigned.length} {isFR ? "scènes non affectées" : "Unassigned Scenes"}</span>
        </div>
        {unassigned.length > 0 ? (
          <p data-novel-story-review-unassigned-note="true" className="ns-card-meta"
            style={{ margin: "12px 0 0" }}>
            {isFR
              ? "Les scènes non affectées sont exclues du manuscrit assemblé. Vérifiez-les dans Structure du livre."
              : "Unassigned Scenes are excluded from the assembled manuscript. Review them in Book Structure."}
          </p>
        ) : null}
      </div>

      <div className="ns-card" data-novel-story-review-matter="true"
        style={{ padding: 20, borderRadius: 18, display: "grid", gap: 10 }}>
        <h3 style={{ margin: 0 }}>{isFR ? "Éléments du livre" : "Book Matter"}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button className="ns-button ns-button-outline" type="button"
            data-novel-story-review-front="true"
            disabled={frontItems.length === 0}
            onClick={() => openSection("front")}>
            {isFR ? "Lire les préliminaires" : "Review Front Matter"} · {frontItems.length}
          </button>
          <button className="ns-button ns-button-outline" type="button"
            data-novel-story-review-back="true"
            disabled={backItems.length === 0}
            onClick={() => openSection("back")}>
            {isFR ? "Lire les éléments de fin" : "Review Back Matter"} · {backItems.length}
          </button>
        </div>
      </div>

      <div className="ns-card" data-novel-story-review-main-manuscript="true"
        style={{ padding: 20, borderRadius: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 6px" }}>
              {isFR ? "MANUSCRIT PRINCIPAL" : "MAIN MANUSCRIPT"}
            </h3>
            <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
              {isFR
                ? "Index limité à 12 chapitres par page, comme dans Textbook Book Review."
                : "The index stays bounded to 12 Chapters per page, as in Textbook Book Review."}
            </p>
          </div>
          <div>
            <label htmlFor="ns-novel-review-jump">{isFR ? "Aller au chapitre" : "Jump to Chapter"}</label>
            <select id="ns-novel-review-jump" className="ns-input"
              data-novel-story-review-chapter-jump="true"
              value={jumpChapterId}
              onChange={(event) => {
                const id = event.target.value;
                setJumpChapterId(id);
                if (id) openSection(`chapter:${id}`);
              }}>
              <option value="">{isFR ? "Choisir…" : "Choose…"}</option>
              {chapterChecks.map((chapter, index) => (
                <option key={chapter.id} value={chapter.id}>
                  {index + 1}. {chapter.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {chapterChecks.length === 0 ? (
          <p className="ns-card-meta" role="status">
            {isFR ? "Aucun chapitre pour le moment. Ajoutez un chapitre dans Structure du livre." : "No Chapters yet. Add a Chapter in Book Structure."}
          </p>
        ) : (
          <>
            <div
              data-novel-story-review-chapter-index="true"
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 270px), 1fr))", gap: 12 }}
            >
              {visibleChapters.map((chapter, index) => {
                const chapterNumber = safeIndexPage * REVIEW_INDEX_PAGE_SIZE + index + 1;
                return (
                  <article
                    key={chapter.id}
                    className="ns-card"
                    data-novel-story-review-chapter-card="true"
                    style={{ padding: 15, borderRadius: 14, border: "1px solid rgba(148,163,184,0.32)" }}
                  >
                    <div className="ns-card-meta">
                      {isFR ? "CHAPITRE" : "CHAPTER"} {chapterNumber}
                    </div>
                    <h4 style={{ margin: "6px 0" }}>{chapter.label}</h4>
                    <p className="ns-card-meta" style={{ margin: "0 0 5px" }}>
                      {chapter.sceneCount} {isFR ? "scènes" : "Scenes"}
                    </p>
                    <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
                      {chapter.ready
                        ? (isFR ? "Prêt à réviser" : "Ready to review")
                        : chapter.sceneCount === 0
                        ? (isFR ? "Aucun texte enregistré" : "No saved writing")
                        : (isFR ? "Vérification nécessaire" : "Needs attention")}
                      {!chapter.ready && chapter.sceneCount > 0 && (chapter.missingCount || chapter.noTextCount)
                        ? ` · ${chapter.missingCount + chapter.noTextCount} ${isFR ? "scènes manquantes ou vides" : "missing or empty Scenes"}`
                        : ""}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <button className="ns-button ns-button-outline" type="button"
                        data-novel-story-review-open-chapter="true"
                        onClick={() => openSection(`chapter:${chapter.id}`)}>
                        {isFR ? "Réviser le chapitre" : "Review Chapter"}
                      </button>
                      <button className="ns-button ns-button-outline" type="button"
                        data-novel-story-review-edit-chapter="true"
                        onClick={() => onOpenChapter?.(chapter.id)}>
                        {isFR ? "Ouvrir le chapitre" : "Open Chapter"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            {pageCount > 1 ? (
              <div
                data-novel-story-review-index-pagination="true"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
                <button className="ns-button ns-button-outline" type="button"
                  disabled={safeIndexPage === 0}
                  onClick={() => setIndexPage(Math.max(0, safeIndexPage - 1))}>
                  {isFR ? "← Groupe précédent" : "← Previous Range"}
                </button>
                <span className="ns-card-meta">{safeIndexPage + 1} / {pageCount}</span>
                <button className="ns-button ns-button-outline" type="button"
                  disabled={safeIndexPage === pageCount - 1}
                  onClick={() => setIndexPage(Math.min(pageCount - 1, safeIndexPage + 1))}>
                  {isFR ? "Groupe suivant →" : "Next Range →"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
